import { colors } from '@ardoq/design-tokens';
import { AnimatedChevron, IconName } from '@ardoq/icons';
import { StatusType, Tag } from '@ardoq/status-ui';
import { DatasourceTable, newTableTheme } from '@ardoq/table-legacy';
import React from 'react';
import styled from 'styled-components';
import { Columns, Row } from './types';
import { GhostButton } from '@ardoq/button';
import { ExpandedRow } from './ExpandedRow';
import { IslandBody, IslandContainer, IslandHeader } from '@ardoq/page-layout';
import { FlexBox } from '@ardoq/layout';
import { type I18nLocale, t } from '@ardoq/i18n';

type SummaryMatrixProps = {
  i18nLocale: I18nLocale;
  columns: Columns;
  data: Row[];
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  status?: StatusType;
  iconName?: IconName;
  footer?: React.ReactNode;
  emptyLabel?: string;
};

const statusToHeaderIconColor = (status: StatusType): string => {
  switch (status) {
    case StatusType.WARNING:
      return colors.iconWarning;
    case StatusType.ERROR:
      return colors.iconError;
    case StatusType.INFO:
      return colors.iconInfo;
    case StatusType.SUCCESS:
      return colors.iconSuccess;
    default:
      return colors.icon;
  }
};

const statusToHeaderIconName = (status: StatusType): IconName => {
  switch (status) {
    case StatusType.WARNING:
      return IconName.WARNING;
    case StatusType.ERROR:
      return IconName.ERROR;
    case StatusType.INFO:
      return IconName.INFO;
    case StatusType.SUCCESS:
    default:
      return IconName.CHECK_CIRCLE;
  }
};

export function SummaryMatrix({
  i18nLocale,
  title = t(i18nLocale, 'integrations:summaryMatrix.title', 'Almost done!'),
  subtitle = t(
    i18nLocale,
    'integrations:summaryMatrix.subtitle',
    'The configuration looks good.'
  ),
  status = StatusType.SUCCESS,
  iconName,
  data,
  columns,
  footer,
  emptyLabel,
}: SummaryMatrixProps) {
  const [expandedRowIds, setExpandedRowIds] = React.useState<Set<string>>(
    new Set()
  );

  const toggleRow = (rowId: string) => {
    const newSet = new Set(expandedRowIds);
    if (expandedRowIds.has(rowId)) newSet.delete(rowId);
    else newSet.add(rowId);
    setExpandedRowIds(newSet);
  };

  return (
    // We're adding a wrapper to workaround in order to put color to the icon
    <IslandContainer>
      <HeaderContainer $iconColor={statusToHeaderIconColor(status)}>
        <IslandHeader
          showHeaderBorder
          title={title}
          subtitle={subtitle}
          iconName={iconName ?? statusToHeaderIconName(status)}
        />
        {/* We want to reuse the header from the package but there's css selector that
         * puts border only when there's a sibling to header.
         */}
        <span></span>
      </HeaderContainer>
      <IslandBody>
        <DatasourceTable
          components={newTableTheme}
          columns={[
            { title: columns.title, dataIndex: 'title' },
            // Columns with counts and status
            ...columns.otherColumns.map((column, index) => ({
              title: column,
              valueRender: (_: any, row: Row) => (
                <Tag
                  iconName={row.columns[index].iconName}
                  statusType={row.columns[index].status}
                >
                  {row.columns[index].count}
                </Tag>
              ),
            })),
            {
              // Chevron button to expand row

              valueRender: (_: any, { _id }: Row) => (
                <FlexBox justify="end">
                  <GhostButton
                    isActive={expandedRowIds.has(_id)}
                    onClick={() => toggleRow(_id)}
                  >
                    <AnimatedChevron $isUp={expandedRowIds.has(_id)} />
                  </GhostButton>
                </FlexBox>
              ),
            },
          ]}
          dataSource={data}
          expandedRowIds={[...expandedRowIds]}
          getExpandedRow={row => (
            <ExpandedRow
              i18nLocale={i18nLocale}
              row={row}
              columnHeadings={columns.otherColumns}
              emptyLabel={emptyLabel}
            />
          )}
        />
      </IslandBody>
      {footer}
    </IslandContainer>
  );
}

const HeaderContainer = styled.div<{ $iconColor: string }>`
  svg {
    color: ${({ $iconColor }) => $iconColor} !important;
  }
`;
